import { Dispatch, SetStateAction, useEffect, useMemo, useReducer, useState } from "react";
import { Box, Grid2 } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { commonLocalization } from "../../../../../../../functions/tableLocalization";
import { renderCateringPricePerUnitCell } from "../../../../../../ServiceManagement/Catering/catering-list.partial";
import { CateringTableRow } from "./CateringTableRow.types";
import {
  handleInitialCatering,
  updateQuantityOfCatering
} from "./BookingService-CateringTable.functions";
import { BookingServiceEditedData, ServiceOrderDto } from "../Domain/BookingServicePrompt.types";
import { CateringServiceSelectCostCenter } from "./BookingService-CateringTable.partial";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { renderTableItemImage } from "../../../../../../Common/ListTablePartial/ListTablePartial.partial";
import { withSeeditMrtInitialState } from "../../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../../themes/use-seedit-mrt-table-options";

type RowSelectAction = {
  type: "update";
  payload: MRT_RowSelectionState;
};

interface RowSelectReducerState {
  selection: MRT_RowSelectionState;
}

type Props = {
  minHeight?: string;
  editedService: BookingServiceEditedData;
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>;
  orderedService: ServiceOrderDto;
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>;
  isMobile: boolean;
  isMandatory: boolean;
  isTabSelected: boolean;
};

export const rowSelectionReducer = (
  state: RowSelectReducerState,
  action: RowSelectAction
): RowSelectReducerState => {
  // currently update is only one type existing
  if (action.type === "update") return { selection: action.payload };

  return state;
};

/**
 * @version 0.1.0
 */
export default function BookingServiceCateringTable({
  minHeight = "60vh",
  editedService,
  setEditedService,
  orderedService,
  setOrderedService,
  isMobile,
  isMandatory,
  isTabSelected
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();

  const isCostExisting = editedService.cateringData.every(menu => menu.pricePerUnit > 0);

  const [state, dispatch] = useReducer(rowSelectionReducer, { selection: {} });

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const getSelectedIds = (selection: MRT_RowSelectionState): string[] =>
    Object.entries(selection)
      .filter(([, isSelected]) => isSelected)
      .map(([key]) => key);

  const getDeselectedIds = (
    newSelection: MRT_RowSelectionState,
    prevSelection: MRT_RowSelectionState
  ): string[] => {
    return Object.keys(prevSelection).filter(key => prevSelection[key] && !newSelection[key]);
  };

  const handleRowSelect = (newSelection: MRT_RowSelectionState) => {
    const selectedIds = getSelectedIds(newSelection);
    const deselectedIds = getDeselectedIds(newSelection, state.selection);

    // get all selected rows from the table
    const selectedRows = table.getRowModel().rows.filter(row => selectedIds.includes(row.id));

    // update quantities
    const updatedCaterings = selectedRows.map(row => ({
      id: Number(row.id),
      qty: row.original.qty,
      name: row.original.name,
      setUpTime: row.original.setUpTime,
      bookingLeadTime: row.original.bookingLeadTime,
      pricePerUnit: row.original.pricePerUnit
    }));

    // Deselect logic: Set qty to 0 for deselected rows
    const deselectedRows = table.getRowModel().rows.filter(row => deselectedIds.includes(row.id));

    deselectedRows.forEach(deselectedRow => {
      updateQuantityOfCatering(
        deselectedRow,
        0,
        editedService.cateringData,
        setEditedService,
        orderedService,
        setOrderedService
      );
    });

    // Update selected rows into states
    setOrderedService(prev => ({ ...prev, caterings: updatedCaterings }));

    // update editedService state
    selectedRows.forEach(row => {
      updateQuantityOfCatering(
        row,
        row.original.qty === 0 ? 1 : row.original.qty,
        editedService.cateringData,
        setEditedService,
        orderedService,
        setOrderedService
      );
    });
  };

  /* istanbul ignore next */
  const handleRowSelectionChange = (newSelection: MRT_RowSelectionState) => {
    handleRowSelect(newSelection);

    // update reducer
    dispatch({ type: "update", payload: newSelection });
  };

  // Whenever the selection changes and handle multiple row IDs
  const handleInitialRowSelection = (newSelectedIds: string[]) => {
    // Create a new selection object and mark the selected IDs as true
    const updatedSelection = { ...state.selection };

    newSelectedIds.forEach(id => {
      updatedSelection[id] = true;
    });

    // update row selection
    dispatch({ type: "update", payload: updatedSelection });
  };

  /* istanbul ignore next */
  const handleChangeCostCenter = (
    event: React.SyntheticEvent,
    value: { id: string; name: string } | null
  ) => {
    if (value) {
      setOrderedService(prev => ({ ...prev, costCenterId: value?.id ?? "no name" }));
    } else {
      setOrderedService(prev => ({ ...prev, costCenterId: null }));
    }
  };

  useEffect(() => {
    if (isTabSelected) {
      // row select once the items are ordered
      handleInitialCatering(
        orderedService,
        editedService,
        setEditedService,
        handleInitialRowSelection,
        handleRowSelectionChange,
        isMandatory
      );
    }
  }, [isTabSelected]);

  const column = useMemo<MRT_ColumnDef<CateringTableRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        enableEditing: false,
        size: 200
      },
      {
        accessorKey: "qty",
        header: t("Qty"),
        size: 150,
        filterVariant: "range",
        muiEditTextFieldProps: ({ row }) => {
          // if it's not orderable, cannot change Qty
          if (!row.original.orderable) return { disabled: true };
          return {
            type: "number",
            value: row.original.qty,
            onChange: event =>
              updateQuantityOfCatering(
                row,
                Number(event.target.value),
                editedService.cateringData,
                setEditedService,
                orderedService,
                setOrderedService
              ),
            inputProps: { step: 1, min: 0 },
            sx: { width: "150px" }
          };
        }
      },
      {
        header: t("Price/Unit"),
        accessorKey: "pricePerUnit",
        enableEditing: false,
        size: 50,
        Cell: ({ row: { original } }) => renderCateringPricePerUnitCell(original.pricePerUnit)
      },
      {
        size: 200,
        enableEditing: false,
        accessorKey: "description",
        header: t("Description")
      },
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          renderTableItemImage(
            cell.row.original.picturePath,
            "catering-item",
            100,
            setShowBiggerImage
          ),
        size: 100
      }
    ],
    [editedService, i18n.language, state]
  );
  const seeditMrtOptions = useSeeditMrtTableOptions<CateringTableRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: editedService.cateringData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "table",
    autoResetPageIndex: false,
    enableEditing: true,
    // if it's not orderable, cannot select
    enableRowSelection: row => row.original.orderable ?? true,
    onRowSelectionChange: updaterOrValue => /* istanbul ignore next */ {
      if (typeof updaterOrValue === "function") {
        // If updaterOrValue is a function, invoke it with the current selection state
        const newSelection = updaterOrValue(state.selection);
        handleRowSelectionChange(newSelection);
      } else {
        // Otherwise, it's a direct RowSelectionState object, so update the selection directly
        handleRowSelectionChange(updaterOrValue);
      }
    },
    getRowId: row => row.id?.toString(),
    state: { isLoading: !editedService.cateringData, rowSelection: state.selection },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no catering")
    }
  });

  return (
    <Box sx={{ minHeight }}>
      <MaterialReactTable table={table} />

      <Grid2 container sx={{ flexDirection: "column" }} data-testid="bookingService-department-box">
        {editedService.cateringData.length > 0 && (
          <CateringServiceSelectCostCenter
            isMobile={isMobile}
            isEditable={false}
            isCostExisting={isCostExisting}
            remoteDepartments={[]}
            cateringData={editedService.cateringData}
            handleChangeCostCenter={handleChangeCostCenter}
          />
        )}
      </Grid2>

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </Box>
  );
}
